import { createEnv } from '@t3-oss/env-nextjs'
import { generateErrorMessage } from 'zod-error'
import { z } from 'zod'
import PrettyError from 'pretty-error'

/**
 * If the environment variable IGNORE_RUNTIME_ENVVARS is set to true,
 * then it will return the schema as optional. Otherwise, it will return the schema as is.
 */
const markAsRuntimeVariable = (schema: z.ZodString | z.ZodURL) => {
  const shouldWrapAsOptional = process.env.IGNORE_RUNTIME_ENVVARS === 'true'
  if (shouldWrapAsOptional) {
    return schema.optional()
  }
  return schema
}

/**
 * Converts a string environment variable to a boolean.
 * @param {"true" | "false"} defaultValue
 * @returns
 */
const booleanEnvironmentSchema = (defaultValue: string) => {
  return z
    .string()
    .default(defaultValue)
    .transform((value) => value === 'true')
}

export const environment = createEnv({
  /**
   * Specify your server-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars.
   */
  server: {
    NODE_ENV: z.enum(['development', 'test', 'production']),
    PORT: z.string().default('8080'),
    ANALYZE: booleanEnvironmentSchema('false'),
    APP_URL: z.url(),
    IGNORE_SENTRY_CONFIG: booleanEnvironmentSchema('false'),
    INDEXER_API_URL: markAsRuntimeVariable(z.url()),
    KTMINE_API_KEY: markAsRuntimeVariable(z.string()),
    KTMINE_API_URL: markAsRuntimeVariable(z.url()),
    ROOT_DOMAIN: z.string().default('nlpatent.com'),
    SENTRY_AUTH_TOKEN: z.string(),
    SENTRY_LOG_LEVEL: z.string().default('INFO'),
    SENTRY_ORG: z.string().default('nlpatent'),
    SENTRY_PROJECT: z.string().optional(),
    STRIPE_SECRET_KEY: z.string(),
    KTMINE_AWS_ACCESS_KEY_ID: markAsRuntimeVariable(z.string()),
    KTMINE_AWS_SECRET_ACCESS_KEY: markAsRuntimeVariable(z.string()),
    SHARE_TOKEN_SECRET: markAsRuntimeVariable(z.string()),
  },

  /**
   * Specify your client-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars. To expose them to the client, prefix them with
   * `NEXT_PUBLIC_`.
   */
  client: {
    NEXT_PUBLIC_ENV: z.enum(['development', 'test', 'staging', 'production']),
    NEXT_PUBLIC_GA_TRACKING_ID: z.string().default(''),
    NEXT_PUBLIC_MONITOR_APP_URL: z.url(),
    NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY: z.string(),
    NEXT_PUBLIC_API_URL: markAsRuntimeVariable(z.url()),
    NEXT_PUBLIC_VISUALIZE_APP_URL: z.url(),
    NEXT_PUBLIC_HELPSCOUT_BEACON_ID: z.string(),
    NEXT_PUBLIC_BUILD_ID: z.string(),
    NEXT_PUBLIC_GITHUB_SHA: z.string(),
  },

  /**
   * You can't destruct `process.env` as a regular object in the Next.js edge runtimes (e.g.
   * middlewares) or client-side so we need to destruct manually.
   */
  runtimeEnv: {
    NODE_ENV: process.env.NODE_ENV,
    PORT: process.env.PORT,
    ANALYZE: process.env.ANALYZE,
    NEXT_PUBLIC_API_URL: process.env.NEXT_PUBLIC_API_URL,
    APP_URL: process.env.APP_URL,
    IGNORE_SENTRY_CONFIG: process.env.IGNORE_SENTRY_CONFIG,
    INDEXER_API_URL: process.env.INDEXER_API_URL,
    KTMINE_API_KEY: process.env.KTMINE_API_KEY,
    KTMINE_API_URL: process.env.KTMINE_API_URL,
    NEXT_PUBLIC_ENV: process.env.NEXT_PUBLIC_ENV,
    NEXT_PUBLIC_GA_TRACKING_ID: process.env.NEXT_PUBLIC_GA_TRACKING_ID,
    NEXT_PUBLIC_MONITOR_APP_URL: process.env.NEXT_PUBLIC_MONITOR_APP_URL,
    NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY:
      process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY,
    ROOT_DOMAIN: process.env.ROOT_DOMAIN,
    SENTRY_AUTH_TOKEN: process.env.SENTRY_AUTH_TOKEN,
    SENTRY_LOG_LEVEL: process.env.SENTRY_LOG_LEVEL,
    SENTRY_ORG: process.env.SENTRY_ORG,
    SENTRY_PROJECT: process.env.SENTRY_PROJECT,
    STRIPE_SECRET_KEY: process.env.STRIPE_SECRET_KEY,
    KTMINE_AWS_ACCESS_KEY_ID: process.env.KTMINE_AWS_ACCESS_KEY_ID,
    KTMINE_AWS_SECRET_ACCESS_KEY: process.env.KTMINE_AWS_SECRET_ACCESS_KEY,
    NEXT_PUBLIC_VISUALIZE_APP_URL: process.env.NEXT_PUBLIC_VISUALIZE_APP_URL,
    NEXT_PUBLIC_HELPSCOUT_BEACON_ID:
      process.env.NEXT_PUBLIC_HELPSCOUT_BEACON_ID,
    NEXT_PUBLIC_BUILD_ID: `${process.env.NEXT_PUBLIC_ENV}-${process.env.NEXT_PUBLIC_GITHUB_SHA}`,
    NEXT_PUBLIC_GITHUB_SHA: process.env.NEXT_PUBLIC_GITHUB_SHA,
    SHARE_TOKEN_SECRET: process.env.SHARE_TOKEN_SECRET,
  },
  /**
   * Run `build` or `dev` with `SKIP_ENV_VALIDATION` to skip env validation. This is especially
   * useful for Docker builds.
   */
  skipValidation: !!process.env.SKIP_ENV_VALIDATION,
  /**
   * Makes it so that empty strings are treated as undefined. `SOME_VAR: z.string()` and
   * `SOME_VAR=''` will throw an error.
   */
  emptyStringAsUndefined: true,
})

const pe = new PrettyError()

/**
 * Function to pretty print a zod error
 */
export const renderZodError = (zodError: z.ZodError, message: string) => {
  // @ts-expect-error - zod-error library is not typed correctly
  const errorMessage = generateErrorMessage(zodError.issues, {
    delimiter: {
      error: '\n\n',
      component: '\n',
    },
    prefix: `${message}:\n\n`,
    transform: ({ errorMessage, index }) =>
      `🔥 Zod schema error number ${index + 1}:\n${errorMessage}`,
  })
  return pe.render(new Error(errorMessage))
}
